// app-pages/AdminPage/AddModel.tsx
"use client";
import { POST_CREATE_MODEL, POST_UPDATE_MODEL } from "api";
import {
  Text,
  Button,
  HStack,
  Input,
  VStack,
  Select,
  Flex,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { fetch } from "libs";
import { ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetModel } from "queries";

interface SocialMediaInput {
  name: string;
  social_media_name: string;
}

interface Tag {
  name: string;
}

export const AddModel = () => {
  const queryClient = useQueryClient();
  const [modelName, setModelName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [modelDescription, setModelDescription] = useState<string>("");
  const [socialMediaInputs, setSocialMediaInputs] = useState<SocialMediaInput[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [modelPicture, setModelPicture] = useState<File | null>(null);
  const [modelThumbnail, setModelThumbnail] = useState<File | null>(null);
  const [tagOptions] = useState(["Virtual", "Clone"]); // Available tag options
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const { data } = useGetModel(false, modelName);
  const [modelCategoryName, setModelCategoryName] = useState<string>("woman");

  useEffect(() => {
    if (data) {
      setDisplayName(data.display_name);
      setModelDescription(data.description ?? "");
      // ... set other fields as needed
      setIsUpdateMode(true); // Switch to update mode
    }
  }, [data]);

  const handleAddTag = (e: ChangeEvent<HTMLSelectElement>) => {
    const newTag = { name: e.target.value };
    if (newTag.name && !tags.some((tag) => tag.name === newTag.name)) {
      setTags([...tags, newTag]);
    }
  };

  // Handler to remove a selected tag
  const removeTag = (tagToRemove: Tag) => {
    setTags(tags.filter((tag) => tag.name !== tagToRemove.name));
  };
  // Function to update the social media inputs
  const updateSocialMediaInput = (index: number, field: string, value: string) => {
    const newInputs = [...socialMediaInputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setSocialMediaInputs(newInputs);
  };

  // Function to add a new input field for social media account
  const addSocialMediaInput = () => {
    if (
      socialMediaInputs.length === 0 ||
      socialMediaInputs[socialMediaInputs.length - 1].name !== "" ||
      socialMediaInputs[socialMediaInputs.length - 1].social_media_name !== ""
    ) {
      setSocialMediaInputs([...socialMediaInputs, { name: "", social_media_name: "" }]);
    }
  };
  const handleModelSubmit = async () => {
    try {
      const formData = new FormData();
      const filteredSocialMedia = socialMediaInputs.filter(
        (input) => input.name !== "" && input.social_media_name !== ""
      );

      const modelData = {
        username: modelName,
        display_name: displayName,
        model_category_name: modelCategoryName,
        description: modelDescription,
        tags: tags,
        social_media_accounts: filteredSocialMedia,
      };

      formData.append("model_data", JSON.stringify(modelData));

      if (modelPicture) {
        formData.append("model_picture", modelPicture);
      }

      if (modelThumbnail) {
        formData.append("model_thumbnail", modelThumbnail);
      }

      if (isUpdateMode && data?.id) {
        const response = await fetch(POST_UPDATE_MODEL(data.id), {
          method: "PUT",
          body: formData,
        });
        console.log(response);
      } else {
        const response = await fetch(POST_CREATE_MODEL, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        queryClient.invalidateQueries({
          queryKey: ["models"],
        });
        console.log("Model created successfully", result);
        // Reset form fields or give user feedback
      }
    } catch (error) {
      console.error("Error creating model:", error);
    }
  };

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null; // Null check
    setModelPicture(file);
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null; // Null check
    setModelThumbnail(file);
  };

  return (
    <Flex direction="column">
      <Text fontWeight="semibold" fontSize="24px" mt="20px">
        Add a new model
      </Text>
      <VStack spacing="4" align="stretch" mt="5">
        <Input
          placeholder="Model Name"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
        />
        <Input
          placeholder="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Stack direction="row" alignItems="center">
          <Text fontWeight={700}>Gender</Text>
          <RadioGroup onChange={setModelCategoryName} value={modelCategoryName}>
            <Stack>
              <Radio value="woman">Female</Radio>
              <Radio value="man">Male</Radio>
            </Stack>
          </RadioGroup>
        </Stack>
        <Input
          placeholder="Description"
          value={modelDescription}
          onChange={(e) => setModelDescription(e.target.value)}
        />
        <HStack>
          <Select placeholder="Select tags" onChange={handleAddTag}>
            {tagOptions.map((option, index) => (
              <option style={{ color: "black" }} key={index} value={option}>
                {option}
              </option>
            ))}
          </Select>
          <HStack spacing={2}>
            {tags.map((tag, index) => (
              <Button key={index} onClick={() => removeTag(tag)}>
                {tag.name} x
              </Button>
            ))}
          </HStack>
        </HStack>
        {socialMediaInputs.map((input, index) => (
          <HStack key={index}>
            <Input
              placeholder="Social Media Account Name"
              value={input.name}
              onChange={(e) => updateSocialMediaInput(index, "name", e.target.value)}
            />
            <Select
              placeholder="Select platform"
              value={input.social_media_name}
              onChange={(e) => updateSocialMediaInput(index, "social_media_name", e.target.value)}
            >
              <option style={{ color: "black" }} value="instagram">
                Instagram
              </option>
              <option style={{ color: "black" }} value="twitter">
                Twitter
              </option>
            </Select>
          </HStack>
        ))}
        <Button onClick={addSocialMediaInput}>Add Another Social Media Account</Button>
        <input type="file" onChange={handlePictureChange} />
        <input type="file" onChange={handleThumbnailChange} />
        <Flex>
          <Button colorScheme="blue" onClick={handleModelSubmit}>
            Create Model
          </Button>
          <Button colorScheme="teal" onClick={handleModelSubmit}>
            Update Model
          </Button>
        </Flex>
      </VStack>
    </Flex>
  );
};

export default AddModel;
